import { useEffect } from 'react';
import { useAppDispatch } from '@/app/hooks';
import { clearWaiterAuth } from './waiterSlice';

/**
 * DF-0193 — le garde 401/403 des surfaces serveur, en UN seul endroit.
 *
 * `WaiterJWTAuthentication` lève `AuthenticationFailed` sans en-tête
 * `WWW-Authenticate`, donc DRF rend **403** (pas 401) pour `WAITER_REVOKED` /
 * `WAITER_EXPIRED` / `WAITER_MODE_DISABLED` — les deux codes comptent. Une
 * erreur réseau ne porte aucun statut HTTP : l'auth est conservée et seule la
 * bannière hors-ligne s'affiche.
 *
 * Le garde était recopié dans `TablesOverview` et `NotesListPage` (SC-381) et
 * dans `TicketPage` (W-T-86) : l'écran ticket, lui, n'avait aucun lecteur sur
 * son SEUL abonnement qui sonde (`printCapabilities`, 60 s), si bien qu'un
 * jeton mort pendant qu'un serveur composait une tournée laissait la tablette
 * tourner à vide jusqu'à ce qu'un humain retape le PIN (37 min mesurées en
 * prod le 2026-09-07). Toute surface qui sonde avec le jeton serveur appelle
 * ce hook, et la suivante en héritera au lieu de rouvrir le trou.
 */
export function isDeadWaiterTokenError(err: unknown): boolean {
  const status = (err as { status?: number | string } | undefined)?.status;
  return status === 401 || status === 403;
}

export function useWaiterAuthGuard(restaurantUuid: string, errors: unknown[]): void {
  const dispatch = useAppDispatch();
  // Booléen dérivé plutôt que le tableau d'erreurs : l'identité d'un objet
  // RTK Query change à chaque tentative, l'effet ne doit se déclencher qu'au
  // basculement vivant → mort (c'est ce que le `authErrorHandled` ref de
  // `TicketPage` faisait à la main).
  const dead = errors.some(isDeadWaiterTokenError);
  useEffect(() => {
    if (dead) dispatch(clearWaiterAuth(restaurantUuid));
  }, [dead, dispatch, restaurantUuid]);
}
